import React, { useState, useCallback } from 'react';
import { TableCell, Box, useTheme } from '@mui/material';
import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { IconButton } from '@/components/atoms/IconButton';
import { Menu, MenuItem } from '@/components/atoms/Menu';
import { TableAction } from './Table.props';

interface TableActionsCellProps<T> {
  row: T;
  index: number;
  actions: TableAction<T>[];
}

interface TableActionsContentProps<T> {
  row: T;
  index: number;
  actions: TableAction<T>[];
}

/**
 * TableActionsContent - Renders just the actions content without TableCell wrapper
 * Used in render methods to avoid nested TableCell elements
 */
export const TableActionsContent = <T,>({
  row,
  index,
  actions,
}: TableActionsContentProps<T>) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleActionClick = useCallback(
    (action: TableAction<T>, e: React.MouseEvent) => {
      e.stopPropagation();
      handleClose();
      action.onClick(row, index);
    },
    [row, index, handleClose]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
    >
      <IconButton
        aria-label="open actions"
        aria-controls={open ? `row-actions-menu-${index}` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
        sx={{ padding: theme.unit(0.5) }}
      >
        <MoreHorizIcon />
      </IconButton>

      <Menu
        id={`row-actions-menu-${index}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {actions.map((action) => {
          const isDisabled = action.disabled?.(row) || false;

          return (
            <MenuItem
              key={action.id}
              disabled={isDisabled}
              onClick={(e: React.MouseEvent) => handleActionClick(action, e)}
            >
              {action.icon && (
                <Box
                  sx={{
                    marginRight: theme.unit(1),
                    display: 'inline-flex',
                  }}
                >
                  {action.icon}
                </Box>
              )}
              {action.label}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

/**
 * TableActionsCell component - Renders the actions menu for a table row
 * Legacy component that wraps TableActionsContent in a TableCell
 */
export const TableActionsCell = <T,>({
  row,
  index,
  actions,
}: TableActionsCellProps<T>) => {
  return (
    <TableCell variant="action">
      <TableActionsContent row={row} index={index} actions={actions} />
    </TableCell>
  );
};
